import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import './index.css';
import initI18n from './i18n';
import { translationsClient } from './lib/translations';
import { logger } from './lib/logger';
import { initAnalytics } from './lib/analytics/initAnalytics';
import { monitorNetworkStatus } from './utils/debugUtils';

// Créer une instance de QueryClient avec la configuration optimisée
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true
    }
  }
});

// Variable globale pour suivre si l'application est déjà montée
window.__APP_MOUNTED = false;

const init = async () => {
  try {
    if (window.__APP_MOUNTED) {
      logger.warn('Application already mounted, skipping initialization', {
        category: 'Initialization'
      });
      return;
    }

    logger.debug('Starting application initialization', {
      category: 'Initialization',
      data: {
        hotelConfig: window.HOTEL_CONFIG || 'Not defined',
        timestamp: new Date().toISOString()
      }
    });

    await initAnalytics();
    monitorNetworkStatus();

    logger.debug('Preloading translations', {
      category: 'Initialization'
    });

    await Promise.all([
      translationsClient.getGeneralTranslations(),
      translationsClient.getCategoryTypeTranslations()
    ]);

    logger.debug('Translations preloaded successfully', {
      category: 'Initialization'
    });

    await initI18n();

    logger.debug('i18n initialized', {
      category: 'Initialization'
    });

    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('Root element not found');
    }

    const root = createRoot(rootElement);

    // Lire le baseUrl depuis la configuration globale
    const baseUrl = window.HOTEL_CONFIG?.baseUrl || '';

    root.render(
      <React.StrictMode>
        <BrowserRouter basename={baseUrl}>
          <QueryClientProvider client={queryClient}>
            <App />
            {import.meta.env.DEV && <ReactQueryDevtools />}
          </QueryClientProvider>
        </BrowserRouter>
      </React.StrictMode>
    );

    window.__APP_MOUNTED = true;

    logger.debug('Application mounted successfully', {
      category: 'Initialization',
      data: {
        timestamp: new Date().toISOString()
      }
    });

  } catch (error) {
    logger.error('Error during application initialization', {
      category: 'Initialization',
      data: {
        error,
        errorDetails: error instanceof Error ? {
          message: error.message,
          stack: error.stack
        } : error,
        timestamp: new Date().toISOString()
      }
    });
    console.error('Failed to initialize application:', error);
  }
};

init().catch(error => {
  logger.error('Unhandled error in init function', {
    category: 'Initialization',
    data: {
      error,
      errorDetails: error instanceof Error ? {
        message: error.message,
        stack: error.stack
      } : error,
      timestamp: new Date().toISOString()
    }
  });
  console.error('Unhandled error in init function:', error);
});